import React from 'react';
import { ArrowUpRight, ArrowDownLeft, Clock, Eye, ArrowRightLeft, Wallet, ShoppingCart, Car, Tag, TrendingUp, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface Transaction {
  id: string;
  amount: number;
  description: string;
  date: string;
  type: 'income' | 'expense' | 'transfer';
  account_id: string;
  to_account_id?: string;
  category_id?: string;
  category?: string;
  payee?: string;
  payee_name?: string;
  currency?: string;
}

interface Account {
  id: string;
  name: string;
  currency?: string;
  color?: string;
}

interface Category {
  id: string;
  name: string;
  icon?: string;
  color?: string;
}

interface RecentActivityWidgetProps {
  transactions: Transaction[];
  accounts: Account[];
  categories?: Category[];
  formatCurrencyWithAbbrev: (amount: number, currency?: string) => string;
  safeT: (key: string) => string;
  defaultCurrency: string;
  convertToDefault: (amount: number, currency: string) => number;
}

export const RecentActivityWidget: React.FC<RecentActivityWidgetProps> = ({
  transactions,
  accounts,
  categories = [],
  formatCurrencyWithAbbrev,
  safeT,
  defaultCurrency,
  convertToDefault
}) => {
  // Enhanced transaction processing similar to mobile app
  const recentTransactions = React.useMemo(() => {
    if (!Array.isArray(transactions)) return [];
    
    return transactions
      .slice(0, 5)
      .map(transaction => {
        const account = accounts.find(acc => acc.id === transaction.account_id);
        const toAccount = transaction.to_account_id ? 
          accounts.find(acc => acc.id === transaction.to_account_id) : undefined;
        const category = categories.find(cat => cat.id === transaction.category_id);
        const accountCurrency = account?.currency || defaultCurrency;
        
        // Determine if it's a transfer
        const isTransfer = transaction.type === 'transfer' || 
                          transaction.to_account_id !== null && transaction.to_account_id !== undefined;
        
        // Get category info with better fallbacks (similar to mobile app logic)
        let categoryName, categoryIcon, categoryColor;
        
        if (isTransfer) {
          const direction = transaction.amount < 0 ? 'out' : 'in';
          const linkedAccountId = direction === 'out' ? transaction.to_account_id : account?.id;
          const linkedAccount = accounts.find(a => a.id === linkedAccountId);
          const accountName = linkedAccount?.name || 'Unknown';
          
          categoryName = direction === 'out'
            ? `Transfer to ${accountName}`
            : `Transfer from ${accountName}`;
          categoryIcon = 'ArrowRightLeft';
          categoryColor = '#22C55E'; // green
        } else if (category) {
          categoryName = category.name;
          categoryIcon = category.icon || 'Tag';
          categoryColor = category.color || '#6B7280';
        } else {
          // Fallback based on transaction type and description
          const description = (transaction.description || '').toLowerCase();
          
          if (description.includes('salary') || description.includes('income')) {
            categoryName = 'Income';
            categoryIcon = 'Wallet';
            categoryColor = '#22C55E';
          } else if (description.includes('grocery') || description.includes('food')) {
            categoryName = 'Food & Dining';
            categoryIcon = 'ShoppingCart';
            categoryColor = '#F59E0B';
          } else if (description.includes('transport') || description.includes('gas')) {
            categoryName = 'Transportation';
            categoryIcon = 'Car';
            categoryColor = '#3B82F6';
          } else {
            categoryName = transaction.type === 'income' ? 'Income' : 'Expense';
            categoryIcon = transaction.type === 'income' ? 'TrendingUp' : 'ShoppingCart';
            categoryColor = transaction.type === 'income' ? '#22C55E' : '#EF4444';
          }
        }
        
        return {
          ...transaction,
          account,
          toAccount,
          category,
          accountCurrency,
          convertedAmount: convertToDefault(Math.abs(transaction.amount), accountCurrency),
          isTransfer,
          categoryName,
          categoryIcon,
          categoryColor,
          title: transaction.description || categoryName || 'Untitled Transaction',
          payeeName: transaction.payee_name || transaction.payee || (isTransfer ? undefined : 'No Payee')
        };
      });
  }, [transactions, accounts, categories, defaultCurrency, convertToDefault]);

  // Helper function to get icon component
  const getIconComponent = (iconName: string) => {
    const iconMap: Record<string, any> = {
      ArrowRightLeft,
      Wallet,
      ShoppingCart,
      Car,
      Tag,
      TrendingUp,
      ArrowUpRight,
      ArrowDownLeft,
      Clock
    };
    return iconMap[iconName] || Tag;
  };

  const formatTransactionAmount = (transaction: any) => {
    const symbol = transaction.type === 'expense' || (transaction.isTransfer && transaction.amount < 0) ? '-' : '+';
    return `${symbol}${formatCurrencyWithAbbrev(transaction.convertedAmount, defaultCurrency)}`;
  };

  const getAmountColor = (transaction: any) => {
    if (transaction.isTransfer) {
      return 'text-secondary dark:text-primary-dark';
    }
    return transaction.type === 'income' ? 'text-positive' : 'text-negative';
  };

  if (recentTransactions.length === 0) {
    return (
      <div className="bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
        <div className="flex items-center justify-between p-6 border-b border-border dark:border-border-dark">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-secondary dark:text-primary-dark mr-2 rtl:ml-2 rtl:mr-0" />
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
              {safeT('dashboard.recentActivity.title') || 'Recent Activity'}
            </h3>
          </div>
          <Link 
            to="/app/transactions"
            className="text-xs text-secondary dark:text-primary-dark hover:text-primary dark:hover:text-secondary transition-colors font-medium"
          >
            {safeT('dashboard.seeAll')}
          </Link>
        </div>
        <div className="p-6 text-center">
          <Clock className="h-12 w-12 text-text-secondary dark:text-text-dark-secondary mx-auto mb-3 opacity-50" />
          <p className="text-text-secondary dark:text-text-dark-secondary text-sm">
            {safeT('dashboard.recentActivity.noTransactions') || 'No recent transactions'}
          </p>
          <p className="text-text-secondary dark:text-text-dark-secondary text-xs mt-1">
            {safeT('dashboard.recentActivity.addFirst') || 'Add your first transaction to get started'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center justify-between p-6 border-b border-border dark:border-border-dark">
        <div className="flex items-center">
          <Clock className="h-5 w-5 text-secondary dark:text-primary-dark mr-2 rtl:ml-2 rtl:mr-0" />
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            {safeT('dashboard.recentActivity.title') || 'Recent Activity'}
          </h3>
        </div>
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <div className="text-xs px-2 py-1 rounded-full bg-secondary/10 dark:bg-primary-dark/10 text-secondary dark:text-primary-dark">
            {safeT('dashboard.realTimeData')}
          </div>
          <Link 
            to="/app/transactions"
            className="text-xs text-secondary dark:text-primary-dark hover:text-primary dark:hover:text-secondary transition-colors font-medium flex items-center"
          >
            {safeT('dashboard.seeAll')}
            <Eye className="h-3 w-3 ml-1 rtl:mr-1 rtl:ml-0" />
          </Link>
        </div>
      </div>
      
      <div className="p-6">
        <div className="space-y-3">
          {recentTransactions.map((transaction, index) => {
            const IconComponent = getIconComponent(transaction.categoryIcon);
            
            return (
              <div
                key={transaction.id}
                className="group flex items-center p-4 rounded-xl border border-border/50 dark:border-border-dark/50 bg-background-secondary/30 dark:bg-background-dark-secondary/30 hover:bg-background-secondary dark:hover:bg-background-dark-secondary hover:border-transparent hover:-translate-y-0.5 transition-all duration-200 hover:shadow-md cursor-pointer"
              >
                {/* Category Icon */}
                <div className={cn(
                  "flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 mr-3 rtl:ml-3 rtl:mr-0",
                  transaction.isTransfer 
                    ? "bg-secondary/10 dark:bg-primary-dark/10" 
                    : transaction.type === 'income' 
                      ? "bg-positive/10 dark:bg-positive/20"
                      : "bg-negative/10 dark:bg-negative/20"
                )}>
                  <IconComponent 
                    className="h-5 w-5" 
                    style={{ color: transaction.categoryColor }}
                  />
                </div>
                
                {/* Transaction Details */}
                <div className="flex-1 min-w-0">
                  {/* Title */}
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary truncate">
                      {transaction.title}
                    </h4>
                    <span className={cn(
                      "text-sm font-bold ml-3 rtl:mr-3 rtl:ml-0 flex-shrink-0",
                      getAmountColor(transaction)
                    )}>
                      {formatTransactionAmount(transaction)}
                    </span>
                  </div>
                  
                  {/* Metadata Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      {/* Category Badge */}
                      {transaction.isTransfer ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-secondary/10 dark:bg-primary-dark/10 text-secondary dark:text-primary-dark">
                          Transfer
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                              style={{ 
                                backgroundColor: `${transaction.categoryColor}15`,
                                color: transaction.categoryColor 
                              }}>
                          {transaction.categoryName}
                        </span>
                      )}
                      
                      {/* Account Badge */}
                      <div className="flex items-center">
                        <div 
                          className="w-2 h-2 rounded-full mr-1 rtl:ml-1 rtl:mr-0" 
                          style={{ backgroundColor: transaction.account?.color || '#6B7280' }}
                        />
                        <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                          {transaction.account?.name || 'Unknown Account'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Date */}
                    <div className="flex items-center text-xs text-text-secondary dark:text-text-dark-secondary">
                      <Calendar className="h-3 w-3 mr-1 rtl:ml-1 rtl:mr-0" />
                      {format(new Date(transaction.date), 'MMM dd')}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Quick Action Footer */}
        <div className="mt-4 pt-4 border-t border-border/20 dark:border-border-dark/20">
          <Link
            to="/app/transactions"
            className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-secondary dark:text-primary-dark hover:text-primary dark:hover:text-secondary bg-secondary/5 dark:bg-primary-dark/5 hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-lg transition-all"
          >
            <Eye className="h-4 w-4 mr-2 rtl:ml-2 rtl:mr-0" />
            {safeT('dashboard.recentActivity.viewAll') || 'View All Transactions'}
          </Link>
        </div>
      </div>
    </div>
  );
}; 